import type { BnplMethodType } from '@/utils/bnpl';
import type { LdCheckoutContext } from '@/utils/ldCheckoutContext';
import { PaymentMethodType } from '@/utils/paymentMethods';

export const LD_EVENTS = {
  paymentMethodImpression: 'ld-payment-method-impression',
  paymentMethodSelected: 'ld-payment-method-selected',
  bookingAttempted: 'ld-booking-attempted',
  paymentAttempted: 'ld-payment-attempted',
  paymentResult: 'ld-payment-result',
} as const;

export type LdEventKey = (typeof LD_EVENTS)[keyof typeof LD_EVENTS];

export type BnplExperimentVariant = 'control' | 'pay-later' | 'now-later';

/**
 * LaunchDarkly event-property filters match flat strings/numbers/booleans only:
 * a `null` is dropped and becomes indistinguishable from an absent key.
 */
export const BNPL_PROVIDER_UNKNOWN = 'unknown';

export type BnplProviderValue = BnplMethodType | typeof BNPL_PROVIDER_UNKNOWN;

const LD_NON_BNPL_METHOD_LABELS = {
  [PaymentMethodType.applePay]: 'apple_pay',
  [PaymentMethodType.googlePay]: 'google_pay',
  [PaymentMethodType.card]: 'card',
  [PaymentMethodType.payInPerson]: 'pay_in_person',
  [PaymentMethodType.cashApp]: 'cash_app',
} as const;

/**
 * The BNPL experiment only cares whether the BNPL option was visible, not
 * which provider backed it — every BNPL provider collapses into this one key.
 */
export const LD_BNPL_SEEN_METHOD = 'bnpl' as const;

export type LdSeenPaymentMethod =
  | (typeof LD_NON_BNPL_METHOD_LABELS)[keyof typeof LD_NON_BNPL_METHOD_LABELS]
  | typeof LD_BNPL_SEEN_METHOD;

/**
 * All *possible* methods, not the runtime-eligible subset — the `_seen` key set
 * has to be statically enumerable so every impression can emit every key.
 */
export const LD_SEEN_PAYMENT_METHODS: readonly LdSeenPaymentMethod[] = [
  ...Object.values(LD_NON_BNPL_METHOD_LABELS),
  LD_BNPL_SEEN_METHOD,
];

export type LdSeenFlags = {
  [Method in LdSeenPaymentMethod as `${Method}_seen`]: boolean;
};

/**
 * Explicitly `false` for every method not shown — LD metric filters cannot
 * distinguish `false` from an absent key, so omitting them breaks the filter.
 */
export const buildLdSeenFlags = (
  shownMethods: Iterable<LdSeenPaymentMethod>,
): LdSeenFlags => {
  const shown = new Set<LdSeenPaymentMethod>(shownMethods);
  const flags = {} as LdSeenFlags;

  LD_SEEN_PAYMENT_METHODS.forEach((method) => {
    flags[`${method}_seen`] = shown.has(method);
  });

  return flags;
};

export type LdEntryType = 'guest' | 'authenticated';

export const BNPL_EXPERIMENT_VARIANTS: readonly BnplExperimentVariant[] = [
  'control',
  'pay-later',
  'now-later',
];

export type LdCommonPayload = {
  variant: BnplExperimentVariant;
  shop_id: string;
  entry_type: LdEntryType;
};

export type LdPaymentMethodImpressionPayload = LdCommonPayload & LdSeenFlags;

export type LdPaymentMethodSelectedPayload = LdCommonPayload & {
  method: string;
  /**
   * Concrete on a control BNPL row, which stands for exactly one provider
   * (SQR-15886); the sentinel for every other method and for the treatment
   * arms' provider-agnostic row, where Stripe resolves the provider later.
   */
  bnpl_provider: BnplProviderValue;
};

export type LdBookingAttemptedPayload = LdCommonPayload & {
  method: string;
  bnpl_provider: typeof BNPL_PROVIDER_UNKNOWN;
};

export type LdPaymentAttemptedPayload = LdCommonPayload & {
  method: string;
  bnpl_provider: BnplProviderValue;
};

export type LdPaymentResultValue =
  | 'success'
  | 'processing'
  | 'declined'
  | 'cancelled'
  | 'error';

export type LdPaymentResultPayload = LdCommonPayload & {
  method: string;
  bnpl_provider: BnplProviderValue;
  result: LdPaymentResultValue;
  decline_reason: string;
};

/**
 * `bnpl_provider` is always the unknown sentinel here: only the BNPL sheet
 * knows the concrete provider, and it builds its own payload. Never emit
 * `null` for this field — LaunchDarkly's event-property filters drop `null`
 * and absent keys alike, making them indistinguishable.
 */
export const buildLdAttemptedPayload = (
  context: LdCheckoutContext,
): LdPaymentAttemptedPayload => ({
  variant: context.variant,
  shop_id: context.shopId,
  entry_type: context.entryType,
  method: context.method,
  bnpl_provider: BNPL_PROVIDER_UNKNOWN,
});

export const buildLdResultPayload = (
  context: LdCheckoutContext,
  result: LdPaymentResultValue,
  declineReason: string,
): LdPaymentResultPayload => ({
  variant: context.variant,
  shop_id: context.shopId,
  entry_type: context.entryType,
  method: context.method,
  bnpl_provider: BNPL_PROVIDER_UNKNOWN,
  result,
  decline_reason: declineReason,
});

/**
 * LaunchDarkly reads numeric metrics only from the `metricValue` argument.
 * Gating it here — rather than at each call site — means declined/errored
 * checkouts never feed their order value into the average-order-value
 * metric, and every present and future caller inherits the fix.
 */
export const resolveLdMetricValue = (
  context: LdCheckoutContext,
  result: LdPaymentResultValue,
): number | undefined =>
  result === 'success' ? context.totalDollars : undefined;

/**
 * A missing decline code must arrive as a real string, not `null` or an
 * absent key — LaunchDarkly's event-property filters drop both alike.
 */
export const DECLINE_REASON_UNKNOWN = 'unknown';
